import { Store } from '@ngrx/store';
import { Auth } from '@shared/auth';
import { Observable } from 'rxjs/Observable';
import { Operator } from 'rxjs/Operator';
import { ErrorTracking } from '../tracking';
import {
  ActionType,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiHttp, ApiPostResponse } from './api-http.service';
import { backend, pushPayload, setPayload, updatePayload } from './backend';
import {
  isDefined,
  Path,
  Query,
  RawStoreListState,
  RawStoreState,
  Reference,
  RootModelEntity,
} from './helpers';
import { RootModelLists } from './root.model';

export class ListObservable<
  T extends keyof RootModelLists,
  R
> extends Observable<R> {
  constructor(
    private ref$: Observable<Reference<T>>,
    private apiHttp: ApiHttp,
    private auth: Auth,
    private store: Store<RawStoreState>,
    private errorTracking: ErrorTracking,
    source: Observable<any>,
  ) {
    super();
    this.source = source;
  }

  lift<Q>(operator: Operator<R, Q>): ListObservable<T, Q> {
    const observable = new ListObservable<T, Q>(
      this.ref$,
      this.apiHttp,
      this.auth,
      this.store,
      this.errorTracking,
      this,
    );
    observable.operator = operator;
    return observable;
  }

  push(object: RootModelEntity<T>): Promise<boolean> {
    return this.ref$
      .take(1)
      .switchMap(ref => {
        const { path, query } = ref;

        const payload = pushPayload(ref, object, this.errorTracking);
        if (!payload) {
          return Observable.of(false);
        }

        return Observable.of(backend[path[0]].post)
          .filter(isDefined)
          .map(post => post(path[1]))
          .do(data => {
            // FIXME: T37425 Works in TS 2.5
            // const action: ActionType<T> = { type: `API_PUSH`, payload };
            const action = { type: `API_PUSH`, payload };
            this.store.dispatch(action);
          })
          .switchMap(config => this.apiHttp.post(config, payload.rawRequest))
          .catch(data => {
            this.sendActions({ type: 'PUSH', payload }, path, query, data);
            return Observable.throw(data);
          })
          .map(data =>
            this.sendActions({ type: 'PUSH', payload }, path, query, data),
          );
      })
      .toPromise();
  }

  set(id: number | string, object: RootModelEntity<T>): Promise<boolean> {
    return this.ref$
      .take(1)
      .switchMap(ref => {
        const { path, query } = ref;

        return Observable.of(backend[path[0]].post)
          .filter(isDefined)
          .map(post => post(path[1]))
          .withLatestFrom(this.store)
          .map(([config, store]) => ({
            config,
            payload: setPayload(ref, id, object, store, this.errorTracking),
          }))
          .filter(isPayloadDefined)
          .do(({ payload }) => {
            // FIXME: T37425 Works in TS 2.5
            // const action: ActionType<T> = { type: `API_SET`, payload };
            const action = { type: `API_SET`, payload };
            this.store.dispatch(action);
          })
          .switchMap(
            ({ config, payload }) =>
              this.apiHttp.post(config, payload.rawRequest),
            ({ payload }, data) => ({ payload, data }),
          )
          .map(({ payload, data }) =>
            this.sendActions({ type: 'SET', payload }, path, query, data),
          );
      })
      .toPromise();
  }

  update(
    id: number | string,
    object: Partial<RootModelEntity<T>>,
  ): Promise<boolean> {
    return this.ref$
      .take(1)
      .withLatestFrom(this.store)
      .map(([ref, store]: [Reference<T>, RawStoreListState]) =>
        updatePayload(ref, id, object, store, this.errorTracking),
      )
      .filter(isDefined)
      .do(({ payload }) => {
        // FIXME: T37425 Works in TS 2.5
        // const action: ActionType<T> = { type: `API_SET`, payload };
        const action = { type: `API_UPDATE`, payload };
        this.store.dispatch(action);
      })
      .switchMap(
        ({ endpoint, payload, method }) => {
          if (method === 'POST') {
            return this.apiHttp.post(
              { endpoint, params: {} },
              payload.rawRequest,
            );
          }
          return this.apiHttp.put({ endpoint, params: {} }, payload.rawRequest);
        },
        ({ payload }, data) => ({ payload, data }),
      )
      .map(({ payload, data }) =>
        this.sendActions(
          { type: 'UPDATE', payload },
          payload.ref.path,
          payload.ref.query,
          data,
        ),
      )
      .toPromise();
  }

  private sendActions(
    baseAction:
      | { type: 'PUSH'; payload: PushRequestPayload<T> }
      | { type: 'SET'; payload: SetRequestPayload<T> }
      | { type: 'UPDATE'; payload: UpdateRequestPayload<T> },
    path: Path<T>,
    query: Query | undefined,
    data: ApiPostResponse<T>,
  ) {
    switch (data.status) {
      case 'success': {
        const action: ActionType<T> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_SUCCESS',
                payload: { ...baseAction.payload, result: data.result },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_SUCCESS',
                  payload: { ...baseAction.payload, result: data.result },
                }
              : {
                  type: 'API_UPDATE_SUCCESS',
                  payload: { ...baseAction.payload, result: data.result },
                };
        this.store.dispatch(action);
        return true;
      }
      case 'error': {
        const action: ActionType<T> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: {
                  ...baseAction.payload,
                  message: data.message,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_ERROR',
                  payload: { ...baseAction.payload, message: data.message },
                }
              : {
                  type: 'API_UPDATE_ERROR',
                  payload: { ...baseAction.payload, message: data.message },
                };
        this.store.dispatch(action);
        console.log(action.payload.type, data.message, path, query);
        return false;
      }
      default: {
        const action: ActionType<T> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: {
                  ...baseAction.payload,
                  message: 'Unexpected API response',
                  error: data,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_ERROR',
                  payload: {
                    ...baseAction.payload,
                    message: 'Unexpected API response',
                  },
                }
              : {
                  type: 'API_UPDATE_ERROR',
                  payload: {
                    ...baseAction.payload,
                    message: 'Unexpected API response',
                  },
                };
        this.store.dispatch(action);
        return false;
      }
    }
  }
}

function isPayloadDefined<C, P>(x: {
  config: C;
  payload?: P;
}): x is { config: C; payload: P } {
  return x.payload !== undefined;
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/list-observable.ts